import { ERROR_MESSAGE_TYPE_DEFAULT } from "../../components/ErrorMessages/ErrorMessages";
import commonApiRequest from "../../utils/commonApiRequest";
import { createEventGA4 } from "../../utils/analytics";
import { downloadFile } from "../../utils/file";
import { isRegistrationRequired } from "../../utils/profile";
import store from "..";
import {
	API_NSP_OPERATION_CODES_COUNTRY,
	API_NSP_OPERATION_CODES_STATE,
	API_NSP_OPERATION_INBOX,
	API_NSP_OPERATION_INBOX_DOCUMENT_BY_ID,
	API_NSP_OPERATION_INBOX_TAX_SUMMARY_BY_ID,
	API_NSP_OPERATION_MAINTENANCE,
	API_NSP_OPERATION_PROFILE,
	API_NSP_OPERATION_PROFILE_CONTACTS_ADDRESSES,
	API_NSP_OPERATION_PROFILE_CONTACTS_EMAILS,
	API_NSP_OPERATION_PROFILE_CONTACTS_PHONES,
	API_NSP_OPERATION_TERMS,
	API_NSP_PROFILE
} from "../../config/api";
import {
	INTERFACE_ERROR_REG,
	SYSTEM_ERROR_500,
	SYSTEM_ERROR_500_RESPONSE_MSG_UUD,
	SYSTEM_ERROR_CNX,
	SYSTEM_ERROR_INV,
	SYSTEM_ERROR_RSP,
	SYSTEM_ERROR_UUD
} from "../../config/common";
import {
	REQUEST_METHOD,
	format,
	isArrayOfLength,
	isFunction,
	isNumber,
	isObject,
	isStringOfLength
} from "@pheaa/channels-component-library";
import { TOKEN_INBOX_DOCUMENT_ID, TOKEN_INBOX_TAX_SUMMARY_ID } from "../../config/tokens";
import { setData, setInboxDocumentData, setInboxTaxSummaryData } from "../actions/profile";

export const clearErrors = () => {
	store.dispatch(setData({
		contactsAddressesErrors: [],
		contactsEmailsErrors: [],
		contactsPhonesErrors: [],
		interfaceErrors: [],
		systemErrors: []
	}));
};

const handleErrors = ({ body, headers, status }, additionalData = {}, errorArrayKey = "systemErrors") => {
	if (isNumber(status)) {
		let responseBody = body;

		if (status === SYSTEM_ERROR_500) {

			try {
				responseBody = JSON.parse(body);
			} catch (e) {
				console.warn("Could not parse response body to JSON.");
			}
		}

		store.dispatch(setData({
			[errorArrayKey]: [{
				code: (status === SYSTEM_ERROR_500 && isObject(responseBody) && isStringOfLength(responseBody.message) && responseBody.message === SYSTEM_ERROR_500_RESPONSE_MSG_UUD) ? SYSTEM_ERROR_UUD : status,
				type: ERROR_MESSAGE_TYPE_DEFAULT
			}],
			...additionalData
		}));
	} else {
		store.dispatch(setData({ [errorArrayKey]: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], ...additionalData }));
		throw new Error("Expected HTTP Error Response Code Not Received.");
	}
};

const handleFailures = (error, additionalData = {}, errorArrayKey = "systemErrors") => {
	if (isObject(error) && isStringOfLength(error.message) && error.message.toLowerCase() === "failed to fetch") {
		store.dispatch(setData({ [errorArrayKey]: [{ code: SYSTEM_ERROR_CNX, type: ERROR_MESSAGE_TYPE_DEFAULT }], ...additionalData }));
	} else {
		store.dispatch(setData({ [errorArrayKey]: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], ...additionalData }));
	}
};

export const getProfile = (getInboxOnSuccess = false) => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_PROFILE }, {
		alwaysCallback: () => { dispatch(setData({ isFetching: false })); },
		beforeCallback: () => { dispatch(setData({ interfaceErrors: [], isFetching: true, isFetchingFailed: false, systemErrors: [] })); },
		errorCallback: ({ body, headers, status }) => {
			if (status === 404) {
				dispatch(setData({ interfaceErrors: [], isFetchingFailed: true, isProfileNotFound: true, systemErrors: [] }));
			} else {
				handleErrors({ body, headers, status }, { isFetchingFailed: true });
			}
		},
		failCallback: error => {
			handleFailures(error, { isFetchingFailed: true });
		},
		successCallback: ({ body }) => {
			try {
				dispatch(setData({ data: body, isProfileNotFound: false }));
				getInboxOnSuccess && !isRegistrationRequired() && dispatch(getInbox());
			} catch (e) {
				dispatch(setData({ systemErrors: [{ code: SYSTEM_ERROR_RSP, type: ERROR_MESSAGE_TYPE_DEFAULT }] }));
			}
		}
	});
};

export const getProfileMaintenanceStatus = () => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_MAINTENANCE }, {
		alwaysCallback: () => {
			dispatch(setData({ isPendingMaintenanceStatus: false }));
		},
		beforeCallback: () => {
			dispatch(setData({ isPendingMaintenanceStatus: true }));
		},
		successCallback: ({ body }) => {
			dispatch(setData({ isMaintenanceMode: body.status === "maintenance" }));
		}
	});
};

export const createProfile = formData => (dispatch, getState) => {
	let requestData = {
		...formData,
		accountIdentifier: formData.accountIdentifier.replace(/\D/g, ""),
		dateOfBirth: format.asDate(formData.dateOfBirth, { outputPattern: "yyyy-mm-dd" })
	};

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_PROFILE }, {
		alwaysCallback: () => { dispatch(setData({ isCreating: false })); },
		beforeCallback: () => { dispatch(setData({ interfaceErrors: [], isCreating: true, systemErrors: [] })); },
		data: requestData,
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { isCreatingFailed: true });
		},
		failCallback: error => {
			handleFailures(error, { isCreatingFailed: true });
		},
		method: REQUEST_METHOD.POST,
		successCallback: ({ body, status }) => {
			const additionalData = { isProfileNotFound: false };

			if (status === 200) {
				additionalData.interfaceErrors = [{ code: INTERFACE_ERROR_REG, type: ERROR_MESSAGE_TYPE_DEFAULT }];
			} else if (status === 201) {
				// GA4 Custom Event;
				createEventGA4("successfulRegistration");

				dispatch(getInbox());
			}

			dispatch(setData({ data: body, ...additionalData }));
		}
	});
};

export const submitTermsAgreement = formData => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_TERMS }, {
		alwaysCallback: () => { dispatch(setData({ isAgreeingTerms: false })); },
		beforeCallback: () => { dispatch(setData({ interfaceErrors: [], isAgreeingTerms: true, systemErrors: [] })); },
		data: formData,
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { isAgreeingTermsFailed: true });
		},
		failCallback: error => {
			handleFailures(error, { isAgreeingTermsFailed: true });
		},
		method: REQUEST_METHOD.POST,
		successCallback: ({ body }) => {
			dispatch(setData({ data: { ...getState().profile.data, ...body } }));
		}
	});
};

export const getCountryCodes = () => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_CODES_COUNTRY }, {
		beforeCallback: () => { dispatch(setData({ isCountryCodesPending: true })); },
		errorCallback: ({ body, headers, status }) => {
			dispatch(setData({ countryCodes: [], isCountryCodesFailed: true, isCountryCodesPending: false }));
		},
		failCallback: error => {
			dispatch(setData({ countryCodes: [], isCountryCodesFailed: true, isCountryCodesPending: false }));
		},
		successCallback: ({ body, headers, status }) => {
			if (isArrayOfLength(body)) {
				dispatch(setData({ countryCodes: body, isCountryCodesPending: false }));
			} else {
				dispatch(setData({ countryCodes: [], isCountryCodesFailed: true, isCountryCodesPending: false }));
			}
		}
	});
};

export const getStateCodes = () => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_CODES_STATE }, {
		beforeCallback: () => { dispatch(setData({ isStateCodesPending: true })); },
		errorCallback: ({ body, headers, status }) => {
			dispatch(setData({ stateCodes: [], isStateCodesFailed: true, isStateCodesPending: false }));
		},
		failCallback: error => {
			dispatch(setData({ stateCodes: [], isStateCodesFailed: true, isStateCodesPending: false }));
		},
		successCallback: ({ body, headers, status }) => {
			if (isArrayOfLength(body)) {
				dispatch(setData({ stateCodes: body, isStateCodesPending: false }));
			} else {
				dispatch(setData({ stateCodes: [], isStateCodesFailed: true, isStateCodesPending: false }));
			}
		}
	});
};

export const updateContactsAddresses = (formData, onSuccess) => (dispatch, getState) => {

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_PROFILE_CONTACTS_ADDRESSES }, {
		beforeCallback: () => {
			dispatch(setData({ contactsAddressesErrors: [], contactsAddressesIsLoading: true }));
		},
		data: formData,
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { contactsAddressesIsLoading: false }, "contactsAddressesErrors");
		},
		failCallback: error => {
			handleFailures(error, { contactsAddressesIsLoading: false }, "contactsAddressesErrors");
		},
		method: REQUEST_METHOD.PUT,
		successCallback: ({ body, headers, status }) => {
			dispatch(setData({ contactsAddressesIsLoading: false }));
			dispatch(getProfile());
			isFunction(onSuccess) && onSuccess(formData);
		}
	});
};

export const updateContactsEmails = (formData, onSuccess) => (dispatch, getState) => {

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_PROFILE_CONTACTS_EMAILS }, {
		beforeCallback: () => {
			dispatch(setData({ contactsEmailsErrors: [], contactsEmailsIsLoading: true }));
		},
		data: formData,
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { contactsEmailsIsLoading: false }, "contactsEmailsErrors");
		},
		failCallback: error => {
			handleFailures(error, { contactsEmailsIsLoading: false }, "contactsEmailsErrors");
		},
		method: REQUEST_METHOD.PUT,
		successCallback: ({ body, headers, status }) => {
			dispatch(setData({ contactsEmailsIsLoading: false }));
			dispatch(getProfile());
			isFunction(onSuccess) && onSuccess(formData);
		}
	});
};

export const updateContactsPhones = (formData, onSuccess) => (dispatch, getState) => {

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_PROFILE_CONTACTS_PHONES }, {
		beforeCallback: () => {
			dispatch(setData({ contactsPhonesErrors: [], contactsPhonesIsLoading: true }));
		},
		data: formData,
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { contactsPhonesIsLoading: false }, "contactsPhonesErrors");
		},
		failCallback: error => {
			handleFailures(error, { contactsPhonesIsLoading: false }, "contactsPhonesErrors");
		},
		method: REQUEST_METHOD.PUT,
		successCallback: ({ body, headers, status }) => {
			dispatch(setData({ contactsPhonesIsLoading: false }));
			dispatch(getProfile());
			isFunction(onSuccess) && onSuccess(formData);
		}
	});
};

export const getInbox = () => (dispatch, getState) => {
	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_INBOX }, {
		beforeCallback: () => {
			dispatch(setData({ inboxErrors: [], inboxIsLoading: true }));
		},
		errorCallback: ({ body, headers, status }) => {
			handleErrors({ body, headers, status }, { inboxIsLoading: false }, "inboxErrors");
		},
		failCallback: error => {
			handleFailures(error, { inboxIsLoading: false }, "inboxErrors");
		},
		successCallback: ({ body, headers, status }) => {
			dispatch(setData({
				inbox: body,
				inboxIsLoading: false
			}));
		}
	});
};

export const getInboxDocumentById = (documentId, mimeType = "application/pdf") => (dispatch, getState) => {
	const tokenReplacements = [{ token: TOKEN_INBOX_DOCUMENT_ID, value: documentId }];

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_INBOX_DOCUMENT_BY_ID, tokenReplacements }, {
		beforeCallback: () => {
			dispatch(setInboxDocumentData({ [documentId]: { errors: [], isLoading: true } }));
		},
		errorCallback: ({ body, headers, status }) => {
			if (isNumber(status)) {
				store.dispatch(setInboxDocumentData({
					[documentId]: { errors: [{ code: status, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			} else {
				store.dispatch(setInboxDocumentData({
					[documentId]: { errors: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
				throw new Error("Expected HTTP Error Response Code Not Received.");
			}
		},
		failCallback: error => {
			if (isObject(error) && isStringOfLength(error.message) && error.message.toLowerCase() === "failed to fetch") {
				store.dispatch(setInboxDocumentData({
					[documentId]: { errors: [{ code: SYSTEM_ERROR_CNX, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			} else {
				store.dispatch(setInboxDocumentData({
					[documentId]: { errors: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			}
		},
		successCallback: ({ body, headers, status }) => {
			const { content, docId, fileName } = body;
			if (isStringOfLength(docId)) {
				dispatch(setInboxDocumentData({ [documentId]: { isLoading: false } }));
				downloadFile(fileName, `data:${mimeType};base64,${content}`);
			}
		}
	});
};

export const getTaxSummaryById = taxSummaryId => (dispatch, getState) => {
	const tokenReplacements = [{ token: TOKEN_INBOX_TAX_SUMMARY_ID, value: taxSummaryId }];

	commonApiRequest({ nsp: API_NSP_PROFILE, operation: API_NSP_OPERATION_INBOX_TAX_SUMMARY_BY_ID, tokenReplacements }, {
		beforeCallback: () => {
			dispatch(setInboxTaxSummaryData({ [taxSummaryId]: { errors: [], isLoading: true } }));
		},
		errorCallback: ({ body, headers, status }) => {
			if (isNumber(status)) {
				store.dispatch(setInboxTaxSummaryData({
					[taxSummaryId]: { errors: [{ code: status, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			} else {
				store.dispatch(setInboxTaxSummaryData({
					[taxSummaryId]: { errors: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
				throw new Error("Expected HTTP Error Response Code Not Received.");
			}
		},
		failCallback: error => {
			if (isObject(error) && isStringOfLength(error.message) && error.message.toLowerCase() === "failed to fetch") {
				store.dispatch(setInboxTaxSummaryData({
					[taxSummaryId]: { errors: [{ code: SYSTEM_ERROR_CNX, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			} else {
				store.dispatch(setInboxTaxSummaryData({
					[taxSummaryId]: { errors: [{ code: SYSTEM_ERROR_INV, type: ERROR_MESSAGE_TYPE_DEFAULT }], isLoading: false }
				}));
			}
		},
		successCallback: ({ body, headers, status }) => {
			const { summaryId } = body;
			if (isStringOfLength(summaryId)) {
				dispatch(setInboxTaxSummaryData({ [taxSummaryId]: { ...body, isLoading: false } }));
			}
		}
	});
};